import {
  aggregateLiveTradeCandles,
  LIVE_CANDLE_BUCKET_SECONDS,
  type LiveTradeCandle,
  type LiveTradeCandleArgs,
} from '@morfi/backend-sdk-v2/chart-presentation';
import { D6_SCALE, D9_SCALE } from '@/config/solana';
import { isoToTimestamp, type TradeLiquidityModel, tradeTapeChartValue } from './sdk-mapping';

export interface LiveTradeSeriesRow {
  readonly eventId: string;
  readonly outcomeIndex: number;
  readonly timestamp?: string | null;
  readonly priceBefore?: string | null;
  readonly priceAfter?: string | null;
  readonly usdcAmount?: string | null;
  readonly liquidityModel?: TradeLiquidityModel | null;
}

interface BuildLiveTradeSeriesArgs {
  readonly trades: ReadonlyArray<LiveTradeSeriesRow>;
  readonly cutoffSec: number;
  readonly mode: 'price' | 'marketCap';
  readonly restrictedOutcomeIndex?: number;
  readonly liveStepLine: boolean;
  readonly fallbackTime?: number;
  readonly fallbackValueByOutcome?: ReadonlyMap<number, number>;
  readonly indexedOutcomeMintSupplyD6ByOutcome?: ReadonlyMap<number, string | null | undefined>;
  readonly initialOutcomeSupplyD6ByOutcome?: ReadonlyMap<number, string | null | undefined>;
  readonly virtualQuoteD6ByOutcome?: ReadonlyMap<number, string | null | undefined>;
}

interface LiveTradeTick {
  readonly eventId: string;
  readonly outcomeIndex: number;
  readonly time: number;
  readonly value: number;
  readonly valueBefore?: number;
}

interface CollectLiveTradeTicksArgs {
  readonly trades: ReadonlyArray<LiveTradeSeriesRow>;
  readonly mode: 'price' | 'marketCap';
  readonly restrictedOutcomeIndex: number | undefined;
  readonly indexedOutcomeMintSupplyD6ByOutcome:
    | ReadonlyMap<number, string | null | undefined>
    | undefined;
  readonly initialOutcomeSupplyD6ByOutcome:
    | ReadonlyMap<number, string | null | undefined>
    | undefined;
  readonly virtualQuoteD6ByOutcome: ReadonlyMap<number, string | null | undefined> | undefined;
}

export { LIVE_CANDLE_BUCKET_SECONDS };

/**
 * Turn the normalized trade tape into per-outcome live-chart points. One
 * pre-window trade is retained to preserve the first visible step's value.
 */
export function buildLiveTradeSeries({
  trades,
  cutoffSec,
  mode,
  restrictedOutcomeIndex,
  liveStepLine,
  fallbackTime,
  fallbackValueByOutcome,
  indexedOutcomeMintSupplyD6ByOutcome,
  initialOutcomeSupplyD6ByOutcome,
  virtualQuoteD6ByOutcome,
}: BuildLiveTradeSeriesArgs): Record<number, Array<{ time: number; value: number }>> {
  const ticks = collectLiveTradeTicks({
    trades,
    mode,
    restrictedOutcomeIndex,
    indexedOutcomeMintSupplyD6ByOutcome,
    initialOutcomeSupplyD6ByOutcome,
    virtualQuoteD6ByOutcome,
  });

  const result: Record<number, Array<{ time: number; value: number }>> = {};
  const inWindowOutcomeIndices = new Set<number>();
  for (const tick of ticks) {
    if (tick.time >= cutoffSec) inWindowOutcomeIndices.add(tick.outcomeIndex);
  }

  for (const outcomeIndex of inWindowOutcomeIndices) {
    const sorted = ticks
      .filter((tick) => tick.outcomeIndex === outcomeIndex)
      .toSorted((left, right) => left.time - right.time || left.value - right.value);
    const preWindow = sorted.filter((tick) => tick.time < cutoffSec).at(-1);
    const inWindow = sorted.filter((tick) => tick.time >= cutoffSec);
    const withBuffer = preWindow === undefined ? inWindow : [preWindow, ...inWindow];

    const points = liveStepLine
      ? withBuffer.flatMap((tick, index) => {
          const valueBefore = withBuffer[index - 1]?.value ?? tick.valueBefore;
          return valueBefore === undefined
            ? [{ time: tick.time, value: tick.value }]
            : [
                { time: tick.time, value: valueBefore },
                { time: tick.time, value: tick.value },
              ];
        })
      : withBuffer.map((tick) => ({ time: tick.time, value: tick.value }));
    const firstInWindow = inWindow[0];
    result[outcomeIndex] =
      preWindow === undefined &&
      firstInWindow?.valueBefore !== undefined &&
      firstInWindow.time > cutoffSec
        ? [{ time: cutoffSec, value: firstInWindow.valueBefore }, ...points]
        : points;
  }

  if (fallbackTime !== undefined && Number.isFinite(fallbackTime)) {
    for (const [outcomeIndex, value] of fallbackValueByOutcome ?? []) {
      if ((result[outcomeIndex]?.length ?? 0) > 0) continue;
      if (!Number.isFinite(value) || value <= 0) continue;
      result[outcomeIndex] = [{ time: fallbackTime, value }];
    }
  }

  return result;
}

/** Convert only at the canvas boundary; SDK aggregation retains integer OHLCV. */
type RenderedLiveCandle = Omit<LiveTradeCandle, 'open' | 'high' | 'low' | 'close'> & {
  readonly open: number;
  readonly high: number;
  readonly low: number;
  readonly close: number;
};

export function buildLiveTradeCandles(
  args: LiveTradeCandleArgs,
): Record<number, RenderedLiveCandle[]> {
  const scale = args.mode === 'price' ? D9_SCALE : D6_SCALE;
  return Object.fromEntries(
    Object.entries(aggregateLiveTradeCandles(args)).map(([index, candles]) => [
      index,
      candles.map((candle) => ({
        ...candle,
        open: Number(candle.open) / scale,
        high: Number(candle.high) / scale,
        low: Number(candle.low) / scale,
        close: Number(candle.close) / scale,
      })),
    ]),
  );
}

function collectLiveTradeTicks({
  trades,
  mode,
  restrictedOutcomeIndex,
  indexedOutcomeMintSupplyD6ByOutcome,
  initialOutcomeSupplyD6ByOutcome,
  virtualQuoteD6ByOutcome,
}: CollectLiveTradeTicksArgs): LiveTradeTick[] {
  const seenEventIds = new Set<string>();
  const ticks: LiveTradeTick[] = [];

  for (const trade of trades) {
    if (seenEventIds.has(trade.eventId)) continue;
    seenEventIds.add(trade.eventId);

    const outcomeIndex = Number(trade.outcomeIndex);
    if (!Number.isFinite(outcomeIndex)) continue;
    if (restrictedOutcomeIndex !== undefined && outcomeIndex !== restrictedOutcomeIndex) continue;

    const time = isoToTimestamp(trade.timestamp ?? '');
    if (!Number.isFinite(time)) continue;
    if (trade.priceAfter === undefined || trade.priceAfter === null) continue;

    const indexedOutcomeMintSupplyD6 = indexedOutcomeMintSupplyD6ByOutcome?.get(outcomeIndex);
    const initialOutcomeSupplyD6 = initialOutcomeSupplyD6ByOutcome?.get(outcomeIndex);
    const virtualQuoteD6 = virtualQuoteD6ByOutcome?.get(outcomeIndex);
    const value = tradeTapeChartValue({
      liquidityModel: trade.liquidityModel,
      priceD9: trade.priceAfter,
      mode,
      indexedOutcomeMintSupplyD6,
      initialOutcomeSupplyD6,
      virtualQuoteD6,
    });
    if (value === undefined) continue;

    const valueBefore = tradeTapeChartValue({
      liquidityModel: trade.liquidityModel,
      priceD9: trade.priceBefore,
      mode,
      indexedOutcomeMintSupplyD6,
      initialOutcomeSupplyD6,
      virtualQuoteD6,
    });
    ticks.push({
      eventId: trade.eventId,
      outcomeIndex,
      time,
      value,
      ...(valueBefore !== undefined ? { valueBefore } : {}),
    });
  }
  return ticks;
}
